/**
 * Numéros NH compacts : NH + MM + YYYY + n° séquentiel (ex. NH052026273).
 * PP et PM ont des séquences indépendantes.
 * Prochain = max(séquence réelle en base / liste) + 1 (pas de plancher figé).
 */
const NH_COMPACT_NUMERO_RE = /^NH(\d{2})(\d{4})(\d+)$/i;

const MATRICULE_PP = '1408941H';
const MATRICULE_PM = '1835448D';

/**
 * Plancher legacy (hors base). CGM1 et CGM2 : vide —
 * la suite vient uniquement du max réel en base (+1).
 * CGM1 en prod : dernier numéro d’honoraire → prochaine = +1 (pas de 272 figé).
 * CGM2 vide → première = 001.
 * À aligner sur backend/services/nhNoteCalculations.js.
 */
const NH_LAST_SEQ_LEGACY = {
  PP: {},
  PM: {},
};

function resolveTypeKey(matricule) {
  return matricule === MATRICULE_PM ? 'PM' : 'PP';
}

function nhSequenceLegacyFloor(mm, yyyy, matricule) {
  const k = String(yyyy);  // ← Juste l'année
  const typeKey = resolveTypeKey(matricule);
  const map = NH_LAST_SEQ_LEGACY[typeKey] || {};
  const v = map[k];
  const n = v == null ? 0 : parseInt(String(v), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function noteNumeroKey(numero) {
  return String(numero || '')
    .replace(/\s/g, '')
    .replace(/^NH/i, 'NH')
    .toUpperCase();
}

export function parseNhCompactNumero(numero) {
  const s = String(numero || '').replace(/\s/g, '');
  const m = s.match(NH_COMPACT_NUMERO_RE);
  if (!m) return null;
  const seq = parseInt(m[3], 10);
  if (Number.isNaN(seq)) return null;
  return { mm: m[1], yyyy: m[2], seq, seqRaw: m[3] };
}

/** Affichage impression / PDF : NH082026562 → NH08.2026-562 (la base reste compacte). */
export function formatNhNumeroForPrint(numero) {
  const s = String(numero || '').replace(/\s/g, '');
  const m = s.match(NH_COMPACT_NUMERO_RE);
  if (!m) return String(numero || '').trim();
  return `NH${m[1]}.${m[2]}-${m[3]}`;
}

export function computeMaxNhCompactSequence(mm, yyyy, rows) {
  let max = 0;
  for (const row of rows || []) {
    const raw = row?.numero ?? row?.numero_nh ?? row?.numeroNH;
    const p = parseNhCompactNumero(raw);
    if (!p) continue;
    if (p.yyyy !== yyyy) continue;  // ← PLUS DE FILTRE PAR MOIS, juste l'année
    if (p.seq > max) max = p.seq;
  }
  return max;
}

function resolveNhAutoDateParts(noteDateStr) {
  const d =
    noteDateStr && /^\d{4}-\d{2}-\d{2}/.test(String(noteDateStr).trim())
      ? String(noteDateStr).trim().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  const yyyy = d.slice(0, 4);
  const mm = d.slice(5, 7);
  if (!/^\d{4}$/.test(yyyy) || !/^\d{2}$/.test(mm)) {
    const now = new Date();
    const y2 = String(now.getFullYear());
    const m2 = String(now.getMonth() + 1).padStart(2, '0');
    const d2 = `${y2}-${m2}-${String(now.getDate()).padStart(2, '0')}`;
    return resolveNhAutoDateParts(d2);
  }
  return { yyyy, mm };
}

/**
 * Filtre les notes par matricule comptable pour ne compter que la séquence du même type.
 * Strict : ne mélange plus CGM1 et CGM2.
 */
function filterRowsByMatricule(rows, matricule) {
  if (!matricule) return rows || [];
  return (rows || []).filter((r) => r.matriculeComptable === matricule);
}

/**
 * Prochain numéro : max(local, séquence max côté serveur) + 1.
 * `matricule` sépare les séquences PP / PM.
 * Format : NH072026001 (séquence sur 3 chiffres min.).
 */
export function nextNhAutoNumeroAfterMerge(noteDateStr, rows, serverMaxSeq = 0, matricule) {
  const { yyyy, mm } = resolveNhAutoDateParts(noteDateStr);
  const filtered = filterRowsByMatricule(rows, matricule);
  const localMax = computeMaxNhCompactSequence(mm, yyyy, filtered);
  const sm = Math.max(0, parseInt(String(serverMaxSeq), 10) || 0);
  const floor = nhSequenceLegacyFloor(mm, yyyy, matricule);
  const max = Math.max(localMax, sm, floor);
  return `NH${mm}${yyyy}${String(max + 1).padStart(3, '0')}`;
}

export function nextNhAutoNumeroCompact(noteDateStr, rows, matricule) {
  return nextNhAutoNumeroAfterMerge(noteDateStr, rows, 0, matricule);
}

export function nextNhDuplicateNumero(noteDateStr, existingRows, serverMaxSeq = 0, matricule) {
  return nextNhAutoNumeroAfterMerge(noteDateStr, existingRows, serverMaxSeq, matricule);
}

/**
 * Max de séquence déjà utilisé en base (GET /api/nh-notes/next-auto-numero).
 * `matricule` + `cabinetId` (CGM2) pour ne pas mélanger les suites.
 */
export async function fetchNhDbMaxSeqForDate(dateStr, matricule, cabinetId) {
  const d = String(dateStr || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return 0;
  try {
    let url = `/api/nh-notes/next-auto-numero?date=${encodeURIComponent(d)}`;
    if (matricule) url += `&matricule=${encodeURIComponent(matricule)}`;
    if (cabinetId != null && Number(cabinetId) > 0) {
      url += `&cabinetId=${encodeURIComponent(Number(cabinetId))}`;
    }
    const r = await fetch(url);
    const j = await r.json();
    if (j && j.success && typeof j.maxSeq === 'number' && Number.isFinite(j.maxSeq)) {
      return Math.max(0, j.maxSeq);
    }
  } catch {
    /* API indisponible */
  }
  return 0;
}

export function resolveUniqueNhNumero(initialNumero, noteDateStr, existingRows, matricule) {
  const prev = existingRows || [];
  const usedKeys = new Set(prev.map((r) => noteNumeroKey(r.numero)));
  const noteDate =
    noteDateStr && /^\d{4}-\d{2}-\d{2}/.test(String(noteDateStr).trim())
      ? String(noteDateStr).trim().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  let numero = String(initialNumero || '').trim();
  if (!numero) {
    numero = nextNhAutoNumeroCompact(noteDate, prev, matricule);
  }
  let guard = 0;
  while (usedKeys.has(noteNumeroKey(numero)) && guard < 100) {
    const p = parseNhCompactNumero(numero);
    if (p) {
      numero = `NH${p.mm}${p.yyyy}${String(p.seq + 1).padStart(3, '0')}`;
    } else {
      const raw = String(numero).replace(/^NH/gi, '').trim();
      const m = raw.match(/^(\d{4})-(\d+)$/);
      if (m) {
        const nextSeq = parseInt(m[2], 10) + 1;
        numero = `NH${m[1]}-${String(nextSeq).padStart(4, '0')}`;
      } else {
        numero = nextNhAutoNumeroCompact(noteDate, prev, matricule);
        break;
      }
    }
    guard += 1;
  }
  return numero;
}
